import { PORTAL_IDS } from './portalIds'

export const PORTAL_AVAILABILITY_RULE_IDS = {
  HIDE_GENERAL_PORTALS_FOR_CLINIC_WORKSTATION_USERS:
    'hideGeneralPortalsForClinicWorkstationUsers',
}

export const PORTAL_AVAILABILITY_OVERRIDES_STORAGE_KEY = 'portalAvailabilityOverrides'

export const CLINIC_WORKSTATION_PORTAL_ROLES = [
  'ROLE_CLINIC_ASSISTANT',
  'ROLE_KIOSK',
  'ROLE_AUMHUM_SCREEN',
  'ROLE_CHAMBER_SCREEN',
  'ROLE_OOH',
]

export const PORTAL_IDS_DISABLED_FOR_CLINIC_WORKSTATION_USERS = [
  PORTAL_IDS.CONSUMER,
  PORTAL_IDS.BUSINESS,
  PORTAL_IDS.PATIENT,
  PORTAL_IDS.PATIENT_PROSPECT,
]

export const DEFAULT_PORTAL_AVAILABILITY_BY_ID =
  PORTAL_IDS_DISABLED_FOR_CLINIC_WORKSTATION_USERS.reduce((availabilityById, portalId) => ({
    ...availabilityById,
    [portalId]: {
      disabledByRules: [
        PORTAL_AVAILABILITY_RULE_IDS.HIDE_GENERAL_PORTALS_FOR_CLINIC_WORKSTATION_USERS,
      ],
    },
  }), {})

const PORTAL_AVAILABILITY_RULES = {
  [PORTAL_AVAILABILITY_RULE_IDS.HIDE_GENERAL_PORTALS_FOR_CLINIC_WORKSTATION_USERS]: {
    isDisabled: (roleSet) =>
      CLINIC_WORKSTATION_PORTAL_ROLES.some((role) => roleSet.has(role)),
  },
}

function normalizePortalIdList(value) {
  return Array.isArray(value)
    ? Array.from(new Set(
      value
        .filter((portalId) => typeof portalId === 'string' && portalId.trim())
        .map((portalId) => portalId.trim()),
    ))
    : []
}

export function normalizePortalAvailabilityOverrides(value) {
  if (typeof value === 'string' && value.trim()) {
    try {
      return normalizePortalAvailabilityOverrides(JSON.parse(value))
    } catch {
      return null
    }
  }

  if (!value || typeof value !== 'object') {
    return null
  }

  const normalized = {
    enabledPortalIds: normalizePortalIdList(value.enabledPortalIds),
    disabledPortalIds: normalizePortalIdList(value.disabledPortalIds),
  }

  return normalized.enabledPortalIds.length > 0 || normalized.disabledPortalIds.length > 0
    ? normalized
    : null
}

export function getStoredPortalAvailabilityOverrides() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }

  try {
    return normalizePortalAvailabilityOverrides(
      window.localStorage.getItem(PORTAL_AVAILABILITY_OVERRIDES_STORAGE_KEY),
    )
  } catch {
    return null
  }
}

export function persistPortalAvailabilityOverrides(overrides) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }

  const normalized = normalizePortalAvailabilityOverrides(overrides)

  try {
    if (normalized) {
      window.localStorage.setItem(
        PORTAL_AVAILABILITY_OVERRIDES_STORAGE_KEY,
        JSON.stringify(normalized),
      )
    } else {
      window.localStorage.removeItem(PORTAL_AVAILABILITY_OVERRIDES_STORAGE_KEY)
    }
  } catch {
    // Storage failures should not block auth or routing.
  }

  return normalized
}

export function persistPortalAvailabilityOverridesFromAuthData(authData) {
  return persistPortalAvailabilityOverrides(authData?.portalAvailabilityOverrides)
}

function resolvePortalAvailabilityOverrides(overrides) {
  return typeof overrides === 'undefined'
    ? getStoredPortalAvailabilityOverrides()
    : normalizePortalAvailabilityOverrides(overrides)
}

export function isPortalExplicitlyEnabledByOverride(portalOrId, overrides) {
  const portalId = getPortalId(portalOrId)
  const normalized = resolvePortalAvailabilityOverrides(overrides)

  return Boolean(portalId && normalized?.enabledPortalIds.includes(portalId))
}

export function isPortalExplicitlyDisabledByOverride(portalOrId, overrides) {
  const portalId = getPortalId(portalOrId)
  const normalized = resolvePortalAvailabilityOverrides(overrides)

  return Boolean(portalId && normalized?.disabledPortalIds.includes(portalId))
}

function applyPortalAvailabilityOverrides(portalId, baseEnabled, overrides) {
  const normalized = resolvePortalAvailabilityOverrides(overrides)

  if (!portalId || !normalized) {
    return baseEnabled
  }

  if (normalized.disabledPortalIds.includes(portalId)) {
    return false
  }

  if (normalized.enabledPortalIds.includes(portalId)) {
    return true
  }

  return baseEnabled
}

function normalizeRoles(userRoles) {
  return Array.isArray(userRoles)
    ? userRoles.filter((role) => typeof role === 'string' && role.trim())
    : []
}

function getPortalId(portalOrId) {
  if (typeof portalOrId === 'string') return portalOrId
  if (portalOrId && typeof portalOrId.id === 'string') return portalOrId.id
  return ''
}

function getPortalAvailability(portalOrId) {
  const portalId = getPortalId(portalOrId)
  const defaultAvailability = DEFAULT_PORTAL_AVAILABILITY_BY_ID[portalId] || {}
  const explicitAvailability =
    portalOrId && typeof portalOrId === 'object' ? portalOrId.availability || {} : {}

  return {
    disabledByRules: Array.from(
      new Set([
        ...(defaultAvailability.disabledByRules || []),
        ...(explicitAvailability.disabledByRules || []),
      ]),
    ),
  }
}

export function getDisabledPortalAvailabilityRules(portalOrId, userRoles) {
  const roleSet = new Set(normalizeRoles(userRoles))
  const availability = getPortalAvailability(portalOrId)

  return availability.disabledByRules.filter((ruleId) => {
    const rule = PORTAL_AVAILABILITY_RULES[ruleId]
    return rule?.isDisabled(roleSet) === true
  })
}

export function isPortalEnabledByAvailability(portalOrId, userRoles, overrides) {
  const portalId = getPortalId(portalOrId)
  const baseEnabled = getDisabledPortalAvailabilityRules(portalOrId, userRoles).length === 0

  return applyPortalAvailabilityOverrides(portalId, baseEnabled, overrides)
}
